import { useState } from 'react';

import { useCitySearchForLeads, useHomeCountryId } from '../use-lead-options';

import { CitySearchPanel } from './CitySearchPanel';

interface DepartureCityPickerProps {
  /// Currently-selected city id (form state). Null when empty.
  value: string | null;
  /// Label to show for the current selection without having to refetch -
  /// usually `"<name> (<isoCode>)"`. Pass `null` when value is null.
  valueLabel: string | null;
  /// Fires with both id and label so the parent form can store the id
  /// AND echo the label without an extra fetch.
  onChange: (next: { id: string | null; label: string | null }) => void;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  label?: string;
}

/**
 * Lead-form Departure-city picker (server-side search, load-more
 * pagination).
 *
 * Thin wrapper over `CitySearchPanel`. The shared `useCitySearchForLeads`
 * hook enforces `isActive=true` + `countryIsActive=true` so deactivating
 * a country immediately hides every city under it from intake. The
 * picker is independent of the lead's DOMESTIC / INTERNATIONAL category:
 * the operator can pick any active city regardless of where the lead
 * is headed.
 */
export const DepartureCityPicker = ({
  value,
  valueLabel,
  onChange,
  disabled,
  required,
  error,
  label = 'Departure city',
}: DepartureCityPickerProps): React.JSX.Element => {
  const [term, setTerm] = useState('');
  // 2026-06-24: departure city is locked to Indian cities only. The
  // home country resolves via `useHomeCountryId` (cached forever);
  // until it resolves we pass null and the picker shows the full
  // active set - the operator's typical typing window is enough to
  // mask the brief flash.
  const homeCountryId = useHomeCountryId();
  const { cities, isLoading, isLoadingMore, hasMore, loadMore } = useCitySearchForLeads({
    term,
    countryId: homeCountryId,
  });

  return (
    <CitySearchPanel
      value={value}
      valueLabel={valueLabel}
      onChange={onChange}
      label={label}
      emptyHint="Showing the first 20 active cities. Type to narrow."
      cities={cities}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      hasMore={hasMore}
      onLoadMore={loadMore}
      term={term}
      onTermChange={setTerm}
      disabled={disabled}
      required={required}
      error={error}
      fieldName="departure-city-search"
    />
  );
};
